/**
 * Supplier Service
 * Handles supplier-related API calls
 */
import BaseApiService from '../base/BaseApiService';
import type { Supplier, SupplierTier, CreateSupplierRequest, SuppliersResponse, Contract } from '@app-types';

class SupplierService extends BaseApiService {
  /**
   * Get suppliers list with optional filters
   */
  async getSuppliers(
    params: {
      limit?: number;
      offset?: number;
      status?: string;
      tierId?: number;
      search?: string;
      name?: string;
      vendorId?: string;
      columnFilters?: string;
    } = {}
  ): Promise<SuppliersResponse> {
    const searchParams = this.buildQueryParams(params);
    return this.get<SuppliersResponse>(`/suppliers?${searchParams}`);
  }

  /**
   * Get all suppliers matching the given filters, ignoring pagination — used for full-dataset export.
   */
  async getSuppliersForExport(
    params: {
      status?: string;
      tierId?: number;
      search?: string;
      name?: string;
      vendorId?: string;
      columnFilters?: string;
    } = {}
  ): Promise<{ total: number; suppliers: Supplier[] }> {
    const searchParams = this.buildQueryParams(params);
    return this.get<{ total: number; suppliers: Supplier[] }>(`/suppliers/export?${searchParams}`);
  }

  /**
   * Get a single supplier by ID
   */
  async getSupplier(id: number): Promise<{ supplier: Supplier }> {
    return this.get<{ supplier: Supplier }>(`/suppliers/${id}`);
  }

  /**
   * Get available supplier tiers
   */
  async getSupplierTiers(): Promise<SupplierTier[]> {
    const response = await this.get<{ count: number; tiers: SupplierTier[] }>('/suppliers/tiers');
    return response.tiers;
  }

  /**
   * Get contracts for a specific supplier
   */
  async getSupplierContracts(
    supplierId: number,
    params?: { limit?: number; offset?: number }
  ): Promise<{
    contracts: Contract[];
    count: number;
    total: number;
    actualTotal: number;
    offset: number;
    limit: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const response = await this.get<{
      count: number;
      total: number;
      actualTotal: number;
      offset: number;
      limit: number;
      supplier_id: number;
      supplier_name: string;
      contracts: Contract[];
    }>(`/suppliers/${supplierId}/contracts?${searchParams}`);

    return {
      contracts: response.contracts,
      count: response.count,
      total: response.total,
      actualTotal: response.actualTotal,
      offset: response.offset,
      limit: response.limit
    };
  }

  /**
   * Create a new supplier
   */
  async createSupplier(data: CreateSupplierRequest): Promise<Supplier> {
    const response = await this.post<{ message: string; supplier: Supplier }>('/suppliers', data);
    return response.supplier;
  }

  /**
   * Update an existing supplier
   */
  async updateSupplier(id: number, data: Partial<Supplier>): Promise<Supplier> {
    const response = await this.put<{ message: string; supplier: Supplier }>(`/suppliers/${id}`, data);
    return response.supplier;
  }

  /**
   * Delete a supplier
   */
  async deleteSupplier(id: number): Promise<void> {
    await this.delete(`/suppliers/${id}`);
  }

  /**
   * Check if a vendor ID exists
   */
  async checkVendorId(vendorId: string): Promise<boolean> {
    const response = await this.get<{ exists: boolean }>(`/suppliers/check-vendor-id/${vendorId}`);
    return response.exists;
  }
}

export const supplierService = new SupplierService();
export default supplierService;
